/**
 * AddMaterialModal - Modal for adding more raw material to an existing session mid-production
 *
 * Features:
 * - Select from available lots for the session's product
 * - Input by weight or by target boxes
 * - Preview: shows if material will be distributed to existing sub-sessions or create a new one
 * - Requires a reason for mid-production material addition
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useLanguage } from '../../../i18n/LanguageContext';
import { X, Info, Plus, Package, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import productionService, { RawMaterial, RawMaterialLot } from '../../../services/productionService';
import { displayRemainingCases } from '../../../utils/lotCases';
import stationManagementService from '../../../services/stationManagementService';
import { formatYield, DEFAULT_YIELD } from '../../../hooks/useYieldFactor';
import { useAuth } from '../../../hooks/useAuth';

interface SubSession {
  sub_session_number: string;
  status: string;
  initial_material_weight: number;
  item_number?: string;
  brand?: string;
}

// Data passed when adding material (to be saved later with main Save button)
export interface AddMaterialData {
  item_number: string;
  transfer_number: string;
  weight: number;
  item_name: string;
  yield_factor: number;
  reason: string;
  reason_other?: string;
  willDistribute: boolean;  // Whether it will distribute to existing sub-sessions or create new
  estimatedBoxes: number;
  targetSubSessionIds?: string[];  // Which sub-sessions to distribute to (if willDistribute=true)
  lot_id?: string;  // WCO-2607: the specific source lot chosen in the per-lot picker
}

interface AddMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionNumber: string;
  productSku: string;
  productName: string;
  productUnitWeight: number;
  subSessions: SubSession[];
  onAddMaterial: (data: AddMaterialData) => void;  // Callback instead of direct save
  pendingAllocations?: AddMaterialData[];  // Pending materials not yet saved to DB
}

// Reason options for adding material mid-session
const ADD_MATERIAL_REASONS = [
  { value: 'insufficient_material', labelKey: 'sessionDetails.addMaterialReasons.insufficient_material' },
  { value: 'production_increase', labelKey: 'sessionDetails.addMaterialReasons.production_increase' },
  { value: 'quality_issue', labelKey: 'sessionDetails.addMaterialReasons.quality_issue' },
  { value: 'supervisor_decision', labelKey: 'sessionDetails.addMaterialReasons.supervisor_decision' },
  { value: 'other', labelKey: 'sessionDetails.addMaterialReasons.other' },
];

// Reason options for the Finish Product tab (UI scaffolding only; backend wired later)
const ADD_FINISH_PRODUCT_REASONS = [
  { value: 'leftover_previous_session', labelKey: 'sessionDetails.addMaterialReasons.leftover_previous_session' },
  { value: 'supervisor_decision', labelKey: 'sessionDetails.addMaterialReasons.supervisor_decision' },
];

type AddMaterialTab = 'raw_material' | 'finish_product';

export default function AddMaterialModal({
  isOpen,
  onClose,
  sessionNumber,
  productSku,
  productName,
  productUnitWeight,
  subSessions,
  onAddMaterial,
  pendingAllocations = []
}: AddMaterialModalProps) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // State
  const [activeTab, setActiveTab] = useState<AddMaterialTab>('raw_material');
  // WCO-2607: raw-material tab now picks a MATERIAL (by item_number), then edits
  // per-lot weights. selectedItemNumber = the chosen raw material; lotWeights maps
  // each usable lot's id → its (editable, capped) weight string.
  const [selectedItemNumber, setSelectedItemNumber] = useState<string>('');
  const [lotWeights, setLotWeights] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [reasonOther, setReasonOther] = useState('');
  const [selectedSubSessionId, setSelectedSubSessionId] = useState<string | null>(null);
  // Finish Product tab state
  const [selectedFinishProduct, setSelectedFinishProduct] = useState('');
  const [finishProductReason, setFinishProductReason] = useState('');
  const [selectedFinishProductSubSessionId, setSelectedFinishProductSubSessionId] = useState<string | null>(null);
  const [isSubmittingFinishProduct, setIsSubmittingFinishProduct] = useState(false);

  // Fetch finish_product_ni lots eligible for this session
  const { data: finishProductLots = [], isLoading: loadingFinishProductLots } = useQuery(
    ['finishProductLots', sessionNumber],
    () => productionService.getFinishProductLots(sessionNumber),
    { enabled: isOpen && activeTab === 'finish_product' && !!sessionNumber }
  );
  
  // Fetch raw materials for this product
  const { data: rawMaterialsData, isLoading: loadingMaterials } = useQuery(
    ['rawMaterials', productSku],
    () => productionService.getRawMaterials(productSku),
    { enabled: isOpen && !!productSku }
  );
  const rawMaterials = rawMaterialsData?.raw_materials ?? [];

  // Deduplicate the aggregate rows to ONE option per raw material (item_number).
  // WCO-2607: the dropdown now chooses a MATERIAL; the individual lots are shown as
  // editable rows below (fetched per-material). Sort by highest yield.
  //
  // getRawMaterials returns TWO rows per material: an AGGREGATED row (current_stock > 0)
  // for materials that have lot inventory, plus a zero-stock "no inventory" row for every
  // linked material (so session-creation can show the full BOM). Here we only want
  // materials that actually have usable stock to add, so filter current_stock > 0 BEFORE
  // deduping — that also guarantees the kept row is the stocked aggregate (real yield),
  // not the zero-stock/zero-yield duplicate.
  const materialOptions = useMemo(() => {
    if (!rawMaterials.length) return [];
    const getYield = (m: RawMaterial) => Number(m.yield_factor) || 0;
    const seen = new Set<string>();
    return rawMaterials
      .filter(m => (Number(m.current_stock) || 0) > 0)
      .filter(m => {
        if (seen.has(m.item_number)) return false;
        seen.add(m.item_number);
        return true;
      })
      .sort((a, b) => getYield(b) - getYield(a));
  }, [rawMaterials]);

  const selectedMaterialData = useMemo(
    () => materialOptions.find(m => m.item_number === selectedItemNumber) ?? null,
    [materialOptions, selectedItemNumber]
  );

  // WCO-2607: fetch the individual usable lots for the selected raw material.
  const { data: rawMaterialLotsData, isLoading: loadingLots } = useQuery(
    ['rawMaterialLots', productSku, selectedItemNumber],
    () => productionService.getRawMaterialLots(productSku, selectedItemNumber),
    { enabled: isOpen && activeTab === 'raw_material' && !!productSku && !!selectedItemNumber }
  );
  // Coerce to an array defensively (guards against an unexpected/absent query result).
  const rawMaterialLots: RawMaterialLot[] = Array.isArray(rawMaterialLotsData) ? rawMaterialLotsData : [];

  // Per-lot remaining cap: current_amount minus any weight already staged (pendingAllocations)
  // against this exact lot, so a lot staged in a prior modal-open shows its reduced ceiling.
  const lotRemainingCap = (lot: RawMaterialLot): number => {
    const pending = pendingAllocations
      .filter(p => p.lot_id === lot.lot_id)
      .reduce((sum, p) => sum + (p.weight || 0), 0);
    return Math.max(0, (Number(lot.current_amount) || 0) - pending);
  };

  // Prefill each lot's weight to its remaining cap when the lot list loads/changes.
  // (useEffect, not useMemo — this is a state side-effect, not a derived value.)
  useEffect(() => {
    if (!rawMaterialLots.length) return;
    setLotWeights(prev => {
      const next = { ...prev };
      let changed = false;
      for (const lot of rawMaterialLots) {
        if (next[lot.lot_id] === undefined) {
          next[lot.lot_id] = lotRemainingCap(lot).toFixed(2);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rawMaterialLots]);

  // Clamp a lot's weight input to [0, remaining cap] on change (HARD cap per requirement).
  const handleLotWeightChange = (lot: RawMaterialLot, value: string) => {
    if (value === '') {
      setLotWeights(prev => ({ ...prev, [lot.lot_id]: '' }));
      return;
    }
    const cap = lotRemainingCap(lot);
    let num = parseFloat(value);
    if (isNaN(num) || num < 0) num = 0;
    if (num > cap) num = cap;
    setLotWeights(prev => ({ ...prev, [lot.lot_id]: String(num) }));
  };

  // Total weight across all lot rows (for the sub-session preview / distribution).
  const totalLotWeight = useMemo(
    () => rawMaterialLots.reduce((sum, lot) => sum + (parseFloat(lotWeights[lot.lot_id] || '') || 0), 0),
    [rawMaterialLots, lotWeights]
  );

  // Check if same material exists in eligible sub-sessions
  const matchingSubSessions = useMemo(() => {
    if (!selectedItemNumber) return [];
    return subSessions.filter(ss =>
      ['pending', 'submitted', 'received', 'ready', 'active', 'paused'].includes(ss.status) &&
      ss.item_number === selectedItemNumber
    );
  }, [selectedItemNumber, subSessions]);

  // Switch material: reset lot weights and sub-session selection.
  const handleMaterialChange = (value: string) => {
    setSelectedItemNumber(value);
    setLotWeights({});
    setSelectedSubSessionId(null);
  };

  // Auto-select the first matching sub-session when material selection changes.
  // (useEffect, not useMemo — state side-effect.)
  useEffect(() => {
    if (matchingSubSessions.length > 0 && selectedSubSessionId === null) {
      setSelectedSubSessionId(matchingSubSessions[0].sub_session_number);
    }
  }, [matchingSubSessions]);

  // Get the selected sub-session for distribution (single selection)
  const targetSubSession = useMemo(() => {
    if (!selectedSubSessionId) return null;
    return matchingSubSessions.find(ss => ss.sub_session_number === selectedSubSessionId) || null;
  }, [matchingSubSessions, selectedSubSessionId]);

  // Preview: will material be distributed or create new sub-session?
  const willDistribute = targetSubSession !== null;

  // Per-lot yield (decimal). Falls back to the aggregate material yield, then DEFAULT_YIELD.
  const lotYield = (lot: RawMaterialLot): number =>
    Number(lot.yield_factor) || Number(selectedMaterialData?.yield_factor) || DEFAULT_YIELD;

  // Per-row estimated OUTPUT boxes = floor(weight * yield / productUnitWeight) — same
  // finished-product box semantics the modal used before (the value flows to onAddMaterial).
  const estBoxesForLot = (lot: RawMaterialLot, weight: number): number =>
    weight > 0 && productUnitWeight > 0
      ? Math.floor((weight * lotYield(lot)) / productUnitWeight)
      : 0;

  // Rows with a positive, in-range weight — the ones that will be staged on submit.
  const activeLotRows = useMemo(() => {
    return rawMaterialLots
      .map(lot => ({ lot, weight: parseFloat(lotWeights[lot.lot_id] || '') || 0 }))
      .filter(r => r.weight > 0);
  }, [rawMaterialLots, lotWeights]);

  // WCO-2607: running totals across the selected lot rows, shown under the lot list
  // when 2+ lots are being added (single lot needs no total — the row already shows it).
  const totalEstUsable = useMemo(
    () => activeLotRows.reduce((sum, r) => sum + r.weight * lotYield(r.lot), 0),
    [activeLotRows]
  );
  const totalEstBoxes = useMemo(
    () => activeLotRows.reduce((sum, r) => sum + estBoxesForLot(r.lot, r.weight), 0),
    [activeLotRows]
  );

  // Validation: a material selected, at least one lot row > 0, every row within its cap,
  // and a reason (+ free-text when 'other'). Caps are already enforced on input, but we
  // re-check here defensively.
  const allRowsWithinCap = rawMaterialLots.every(lot => {
    const w = parseFloat(lotWeights[lot.lot_id] || '') || 0;
    return w <= lotRemainingCap(lot);
  });
  const isValid = !!selectedItemNumber && activeLotRows.length > 0 && allRowsWithinCap &&
    !!reason && (reason !== 'other' || !!reasonOther.trim());

  // Handle submit — stage ONE AddMaterialData per non-zero lot row. The parent's
  // handleAddMaterial pushes each into pendingMaterials[]; the Save loop POSTs each with
  // its own lot_id (single-lot backend path, now lot-precise). No parent-handler change.
  const handleSubmit = () => {
    if (!isValid || !selectedMaterialData) return;

    for (const { lot, weight } of activeLotRows) {
      onAddMaterial({
        item_number: selectedItemNumber,
        transfer_number: lot.transfer_number,
        lot_id: lot.lot_id,
        weight,
        item_name: lot.item_english_name || selectedMaterialData.item_english_name || selectedItemNumber,
        yield_factor: lotYield(lot),
        reason,
        reason_other: reason === 'other' ? reasonOther.trim() : undefined,
        willDistribute,
        estimatedBoxes: estBoxesForLot(lot, weight),
        targetSubSessionIds: willDistribute && selectedSubSessionId ? [selectedSubSessionId] : undefined
      });
    }

    toast.success(
      willDistribute
        ? t('sessionDetails.materialAddedToExisting').replace('{{weight}}', totalLotWeight.toFixed(2))
        : t('sessionDetails.materialAddedAsNew').replace('{{weight}}', totalLotWeight.toFixed(2))
    );

    handleClose();
  };

  // Reset form when modal closes
  const handleClose = () => {
    setActiveTab('raw_material');
    setSelectedItemNumber('');
    setLotWeights({});
    setReason('');
    setReasonOther('');
    setSelectedSubSessionId(null);
    setSelectedFinishProduct('');
    setFinishProductReason('');
    setSelectedFinishProductSubSessionId(null);
    setIsSubmittingFinishProduct(false);
    onClose();
  };

  // All sub-sessions eligible to receive a finish product: everything that is not
  // completed/cancelled. Whitelist matches the backend /add-finish-product validation
  // so the dropdown never offers a sub-session the backend would reject.
  const eligibleSubSessionsForFinishProduct = useMemo(
    () => subSessions.filter(ss => ['pending', 'submitted', 'received', 'ready', 'active', 'paused'].includes(ss.status)),
    [subSessions]
  );

  // Resolve which sub-session the finish product is added to:
  // - exactly 1 eligible -> auto-target it (no dropdown shown)
  // - 2+ eligible        -> use the supervisor's explicit pick (WCO-2368)
  const finishProductTargetSubSession = useMemo(() => {
    if (eligibleSubSessionsForFinishProduct.length === 1) return eligibleSubSessionsForFinishProduct[0];
    return eligibleSubSessionsForFinishProduct.find(ss => ss.sub_session_number === selectedFinishProductSubSessionId) || null;
  }, [eligibleSubSessionsForFinishProduct, selectedFinishProductSubSessionId]);

  const isFinishProductValid =
    !!selectedFinishProduct &&
    !!finishProductReason &&
    !!finishProductTargetSubSession &&
    !isSubmittingFinishProduct;

  const handleAddProduct = async () => {
    if (!isFinishProductValid || !finishProductTargetSubSession) return;
    setIsSubmittingFinishProduct(true);
    try {
      const result = await stationManagementService.addFinishProductToSession(
        sessionNumber,
        finishProductTargetSubSession.sub_session_number,
        selectedFinishProduct,
        finishProductReason as 'leftover_previous_session' | 'supervisor_decision',
        user?.id
      );
      toast.success(result?.message || (t('sessionDetails.finishProductAdded') || 'Finish product allocated to session'));
      queryClient.invalidateQueries(['session-details', sessionNumber]);
      queryClient.invalidateQueries(['finishProductLots', sessionNumber]);
      handleClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to allocate finish product');
    } finally {
      setIsSubmittingFinishProduct(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]" onClick={(e) => e.stopPropagation()}>
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden relative flex flex-col">
        {/* Header - fixed at top */}
        <div className="flex justify-between items-center p-6 pb-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('sessionDetails.addMaterial') || 'Add Raw Material'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {sessionNumber} - {productName}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content - middle section with padding-bottom for footer */}
        <div className="flex-1 overflow-y-auto p-6 pb-24">

        {/* Tab strip */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('raw_material')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'raw_material'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
          >
            {t('sessionDetails.addMaterialTabRawMaterial') || 'Raw Material'}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('finish_product')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'finish_product'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
          >
            {t('sessionDetails.addMaterialTabFinishProduct') || 'Finish Product'}
          </button>
        </div>

        {activeTab === 'raw_material' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-0">

        {/* LEFT COLUMN: pick the material + edit per-lot weights */}
        <div>

        {/* Material Selection — choose which raw material; lots are listed below (WCO-2607) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('sessionDetails.selectLot') || 'Select Material'} <span className="text-red-500">*</span>
          </label>
          {loadingMaterials ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <select
              value={selectedItemNumber}
              onChange={(e) => handleMaterialChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">{t('common.select') || 'Select'}...</option>
              {materialOptions.map((material) => {
                const yieldFactor = Number(material.yield_factor) || 0; // Decimal from backend (guaranteed by resolve_yield_factor)
                // Aggregated available weight for this material = current_stock (SUM of usable-lot
                // current_amount, same predicate as the /raw-material-lots rows below), minus any
                // weight already staged (pendingAllocations) against this material's lots.
                const pendingWeight = pendingAllocations
                  .filter(p => p.item_number === material.item_number)
                  .reduce((sum, p) => sum + (p.weight || 0), 0);
                const available = Math.max(0, (Number(material.current_stock) || 0) - pendingWeight);
                return (
                  <option key={material.item_number} value={material.item_number} disabled={available <= 0}>
                    [{formatYield(yieldFactor, undefined, material.yield_factor_source)}] {material.item_number} - {material.item_english_name} ({available.toFixed(2)} {material.unit || 'lb'})
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {/* Per-lot rows: one editable weight input per usable lot (WCO-2607) */}
        {selectedItemNumber && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('sessionDetails.selectLotsToAdd') || 'Select lots to add'} <span className="text-red-500">*</span>
            </label>
            {loadingLots ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : rawMaterialLots.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                {t('sessionDetails.noLotsAvailable') || 'No usable lots available for this material'}
              </p>
            ) : (<>
              {/* Column captions (labels live here once instead of per compact row) */}
              <div className="flex items-center gap-3 px-2 pb-1 text-xs text-gray-400 dark:text-gray-500">
                <span className="flex-1">{t('sessionDetails.selectLot') || 'Lot'}</span>
                <span className="w-28 shrink-0 text-right">{t('sessionDetails.perLotWeight') || 'Weight (lbs)'}</span>
                <span className="w-28 shrink-0 text-right">{t('sessionDetails.estUsable') || 'Est. Usable'} / {t('sessionDetails.estOutputBoxes') || 'output boxes'}</span>
              </div>
              {
              // Cap the lot list height and scroll internally, so the sub-session selector,
              // preview, reason, and footer stay visible regardless of lot count (WCO-2607).
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {rawMaterialLots.map((lot) => {
                  const cap = lotRemainingCap(lot);
                  const weight = parseFloat(lotWeights[lot.lot_id] || '') || 0;
                  const estUsable = weight * lotYield(lot);
                  const estBoxes = estBoxesForLot(lot, weight);
                  const rawUnitWeight = Number(lot.unit_weight) || 0;
                  // WCO-2594: for a lot with a received case count, show REMAINING cases derived from live
                  // weight (displayRemainingCases — lot's own cases:weight ratio, catch-weight-safe, FLOOR)
                  // instead of the frozen received actual_quantity, which never decrements on consumption and
                  // would overstate what's left. Fall back to the received count only if the lot has no weight
                  // basis. Preserves legacy behavior at the edges: actual_quantity === 0 (e.g. a not-intact
                  // remnant, tracked as loose pieces not cases) stays 0; a null count keeps the unit_weight estimate.
                  const cases = lot.actual_quantity != null
                    ? (displayRemainingCases(lot) ?? Number(lot.actual_quantity))
                    : (rawUnitWeight > 0 ? Math.floor((Number(lot.current_amount) || 0) / rawUnitWeight) : null);
                  // Status badge label
                  const status = lot.status || 'available';
                  const badge = lot.is_ni_remnant
                    ? (t('sessionDetails.lotNotIntact') || 'Not-intact')
                    : status === 'by_product_p'
                      ? (t('sessionDetails.lotByProduct') || 'By-product')
                      : status === 'on_hold'
                        ? (t('sessionDetails.lotOnHold') || 'On-hold')
                        : (t('sessionDetails.lotIntact') || 'Intact');
                  const badgeClass = lot.is_ni_remnant
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    : status === 'by_product_p'
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                      : status === 'on_hold'
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';

                  return (
                    // Compact single-row card: identity on the left, weight input + est.
                    // usable + est. boxes inline on the right (WCO-2607).
                    <div
                      key={lot.lot_id}
                      className="flex items-center gap-3 p-2 border border-indigo-200 dark:border-indigo-700/50 rounded-lg bg-indigo-50/50 dark:bg-indigo-900/20"
                    >
                      {/* Identity */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {lot.transfer_number}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${badgeClass}`}>
                            {badge}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {(Number(lot.current_amount) || 0).toFixed(2)} lbs
                          {cases != null && ` · ${(t('sessionDetails.lotCases') || '{{n}} cases').replace('{{n}}', String(cases))}`}
                        </div>
                      </div>

                      {/* Weight input */}
                      <div className="w-28 shrink-0">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max={cap}
                          value={lotWeights[lot.lot_id] ?? ''}
                          onChange={(e) => handleLotWeightChange(lot, e.target.value)}
                          placeholder="0.0"
                          title={t('sessionDetails.perLotWeight') || 'Weight (lbs)'}
                          className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-right"
                        />
                      </div>

                      {/* Est. usable + est. boxes (stacked, compact) */}
                      <div className="w-28 shrink-0 text-right leading-tight">
                        <div className="text-sm font-medium text-green-600 dark:text-green-400" title={t('sessionDetails.estUsable') || 'Est. Usable (lbs)'}>
                          {estUsable > 0 ? estUsable.toFixed(2) : '-'}
                        </div>
                        <div className="text-xs text-indigo-600 dark:text-indigo-400" title={`${t('sessionDetails.estBoxesPrefix') || 'Est.'} ${t('sessionDetails.estOutputBoxes') || 'output boxes'}`}>
                          {estBoxes > 0 ? `${estBoxes} ${t('sessionDetails.boxesUnit') || 'boxes'}` : '-'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              }
              {/* Running total across the selected lots (only when 2+ lots are added) */}
              {activeLotRows.length >= 2 && (
                <div className="flex items-center gap-3 px-2 pt-2 mt-1 border-t border-indigo-200 dark:border-indigo-700/50">
                  <span className="flex-1 text-sm font-semibold text-gray-900 dark:text-white">
                    {t('sessionDetails.total') || 'Total'}
                    <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                      ({activeLotRows.length} {t('sessionDetails.lotsUnit') || 'lots'})
                    </span>
                  </span>
                  <span className="w-28 shrink-0 text-right text-sm font-semibold text-gray-900 dark:text-white">
                    {totalLotWeight.toFixed(2)}
                  </span>
                  <span className="w-28 shrink-0 text-right leading-tight">
                    <span className="block text-sm font-semibold text-green-600 dark:text-green-400">
                      {totalEstUsable > 0 ? totalEstUsable.toFixed(2) : '-'}
                    </span>
                    <span className="block text-xs text-indigo-600 dark:text-indigo-400">
                      {totalEstBoxes > 0 ? `${totalEstBoxes} ${t('sessionDetails.boxesUnit') || 'boxes'}` : '-'}
                    </span>
                  </span>
                </div>
              )}
            </>)}
          </div>
        )}

        </div>
        {/* END LEFT COLUMN */}

        {/* RIGHT COLUMN: target sub-session, preview, and reason */}
        <div>

        {/* Sub-session selection when 1+ matching sub-sessions exist */}
        {selectedItemNumber && matchingSubSessions.length >= 1 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('sessionDetails.selectTargetSubSession') || 'Select Target Sub-Session'}
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2">
              {matchingSubSessions.map(ss => {
                const isSelected = selectedSubSessionId === ss.sub_session_number;
                return (
                  <label
                    key={ss.sub_session_number}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border border-transparent'
                    }`}
                  >
                    <input
                      type="radio"
                      name="targetSubSession"
                      checked={isSelected}
                      onChange={() => setSelectedSubSessionId(ss.sub_session_number)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {ss.sub_session_number}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        ({(ss.initial_material_weight || 0).toFixed(2)} lbs)
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      ss.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      ss.status === 'paused' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                      'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {t(`status.${ss.status}`) || ss.status}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Preview — summarizes the total across all staged lot rows (WCO-2607) */}
        {selectedItemNumber && totalLotWeight > 0 && (
          <div className={`mb-4 p-3 rounded-lg border ${
            willDistribute
              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
              : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
          }`}>
            <div className="flex items-start gap-2">
              <Info className={`w-4 h-4 mt-0.5 ${willDistribute ? 'text-blue-500' : 'text-green-500'}`} />
              <div className="text-sm">
                {willDistribute && targetSubSession ? (
                  <>
                    <p className="font-medium text-blue-700 dark:text-blue-300">
                      {t('sessionDetails.willAddToExisting') || 'Will add to existing sub-session:'}
                    </p>
                    <p className="mt-1 text-blue-600 dark:text-blue-400">
                      • {targetSubSession.sub_session_number}: {(targetSubSession.initial_material_weight || 0).toFixed(2)} → {((targetSubSession.initial_material_weight || 0) + totalLotWeight).toFixed(2)} lbs (+{totalLotWeight.toFixed(2)})
                    </p>
                    <p className="mt-0.5 text-xs text-blue-500 dark:text-blue-400">
                      {activeLotRows.length} {activeLotRows.length === 1 ? 'lot' : 'lots'}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-green-700 dark:text-green-300">
                      <Plus className="w-4 h-4 inline mr-1" />
                      {t('sessionDetails.willCreateNew') || 'Will create new sub-session:'}
                    </p>
                    <p className="text-green-600 dark:text-green-400 mt-1">
                      • {totalLotWeight.toFixed(2)} lbs ({activeLotRows.length} {activeLotRows.length === 1 ? 'lot' : 'lots'})
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('sessionDetails.addMaterialReason') || 'Reason'} <span className="text-red-500">*</span>
          </label>
          <select
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value !== 'other') setReasonOther('');
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">{t('sessionDetails.selectAddMaterialReason') || 'Select reason...'}</option>
            {ADD_MATERIAL_REASONS.map(r => (
              <option key={r.value} value={r.value}>
                {t(r.labelKey) || r.value.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          {reason === 'other' && (
            <input
              type="text"
              value={reasonOther}
              onChange={(e) => setReasonOther(e.target.value)}
              placeholder={t('sessionDetails.addMaterialOtherPlaceholder') || 'Enter reason...'}
              className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          )}
        </div>

        </div>
        {/* END RIGHT COLUMN */}

        </div>
        )}

        {activeTab === 'finish_product' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-0">

            {/* LEFT COLUMN: pick the finish product (+ target sub-session) */}
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('sessionDetails.selectProduct') || 'Select Product'} <span className="text-red-500">*</span>
                </label>
                {loadingFinishProductLots ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <select
                    value={selectedFinishProduct}
                    onChange={(e) => setSelectedFinishProduct(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">{t('common.select') || 'Select'}...</option>
                    {finishProductLots.map(lot => (
                      <option key={lot.lot_id} value={lot.lot_id}>
                        {lot.item_number} — {lot.item_english_name || ''} ({Number(lot.current_amount).toFixed(2)} lbs)
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* WCO-2368: when 2+ sub-sessions are eligible, let the supervisor pick which
                  one receives the finish product. A single eligible sub-session is targeted
                  automatically (no dropdown). */}
              {eligibleSubSessionsForFinishProduct.length >= 2 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('sessionDetails.selectTargetSubSession') || 'Select Target Sub-Session'} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedFinishProductSubSessionId || ''}
                    onChange={(e) => setSelectedFinishProductSubSessionId(e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">{t('sessionDetails.selectFinishProductSubSession') || 'Select sub-session...'}</option>
                    {eligibleSubSessionsForFinishProduct.map(ss => (
                      <option key={ss.sub_session_number} value={ss.sub_session_number}>
                        {[ss.sub_session_number, ss.item_number, ss.brand].filter(Boolean).join(' · ')}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {/* END LEFT COLUMN */}

            {/* RIGHT COLUMN: reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('sessionDetails.addMaterialReason') || 'Reason'} <span className="text-red-500">*</span>
              </label>
              <select
                value={finishProductReason}
                onChange={(e) => setFinishProductReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">{t('sessionDetails.selectAddMaterialReason') || 'Select reason...'}</option>
                {ADD_FINISH_PRODUCT_REASONS.map(r => (
                  <option key={r.value} value={r.value}>
                    {t(r.labelKey) || r.value.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            {/* END RIGHT COLUMN */}

          </div>
        )}

        </div>
        {/* End Scrollable Content */}

        {/* Actions - Absolutely positioned Footer */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-end gap-3 p-6 pt-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          {activeTab === 'raw_material' ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isValid}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Package className="w-4 h-4" />
              {t('sessionDetails.confirmAddMaterial') || 'Add Material'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleAddProduct}
              disabled={!isFinishProductValid}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Package className="w-4 h-4" />
              {t('sessionDetails.addProduct') || 'Add Product'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
